// modals/ObjectiveForm.js - VERSION AVEC UNITÉS DANS LES PLACEHOLDERS ET PRIORITÉ CORRIGÉE
import {
    AlertCircle,
    Check,
    ChevronDown
} from 'lucide-react';
import { useState } from 'react';

const ObjectiveForm = ({
    formData,
    errors,
    isSubmitting,
    categoryOptions,
    priorityOptions,
    selectedCategory,
    onInputChange,
    onSubmit,
    onClose,
    editMode = false,
    hideCategorySelector = false
}) => {
    const [localErrors, setLocalErrors] = useState({});
    const [customUnit, setCustomUnit] = useState(''); // Pour les unités personnalisées
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);
    const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);

    const timelineOptions = [
        { value: 'semaines', label: 'Semaines' },
        { value: 'mois', label: 'Mois' },
        { value: 'années', label: 'Années' }
    ];

    // Fonction pour générer le placeholder avec unité
    const getPlaceholderWithUnit = (baseText, isTarget = false) => {
        let unit = '';
        if (formData.valueUnit && formData.valueUnit !== 'custom') {
            unit = formData.valueUnit;
        } else if (formData.valueUnit === 'custom' && customUnit) {
            unit = customUnit;
        }

        if (unit) {
            return `${baseText} (${unit})`;
        }
        return baseText;
    };

    const validateForm = () => {
        const newErrors = {};

        // VALIDATION DU TITRE (OBLIGATOIRE)
        if (!formData.title.trim()) {
            newErrors.title = 'Veuillez saisir un titre pour votre objectif';
        }

        if (!formData.category) {
            newErrors.category = 'Veuillez sélectionner une catégorie';
        }

        if (formData.category === 'autres' && !formData.customCategory.trim()) {
            newErrors.customCategory = 'Veuillez saisir un nom de catégorie';
        }

        // VALIDATION DE LA PRIORITÉ
        if (!formData.priority || !['low', 'medium', 'high'].includes(formData.priority)) {
            newErrors.priority = 'Veuillez sélectionner une priorité';
        }

        // CORRECTION: Autoriser 0 comme valeur actuelle
        if (formData.valueType === 'text') {
            if (!formData.current && formData.current !== '') {
                newErrors.current = 'Veuillez saisir une valeur actuelle';
            }
        } else {
            // Pour les valeurs numériques, vérifier si c'est vide ou si la valeur est négative
            if (formData.current === '' || formData.current === null || formData.current === undefined) {
                newErrors.current = 'Veuillez saisir une valeur actuelle';
            } else if (parseFloat(formData.current) < 0) {
                newErrors.current = 'La valeur actuelle ne peut pas être négative';
            }
        }

        // VALIDATION DE L'OBJECTIF
        if (formData.valueType === 'text') {
            if (!formData.target || !formData.target.trim()) {
                newErrors.target = 'Veuillez saisir un objectif valide';
            }
        } else {
            if (!formData.target || parseFloat(formData.target) <= 0) {
                newErrors.target = 'Veuillez saisir un objectif valide';
            }
        }

        // Validation spécifique selon le type de valeur
        if (formData.valueType === 'percentage') {
            if (formData.current !== '' && formData.current !== null && formData.current !== undefined) {
                const currentVal = parseFloat(formData.current);
                if (currentVal < 0 || currentVal > 100) {
                    newErrors.current = 'Le pourcentage doit être entre 0 et 100';
                }
            }
            if (formData.target && (parseFloat(formData.target) < 0 || parseFloat(formData.target) > 100)) {
                newErrors.target = 'Le pourcentage doit être entre 0 et 100';
            }
        }

        // CORRECTION: Vérifier la comparaison uniquement si les deux valeurs sont valides
        if (formData.valueType === 'number' &&
            formData.current !== '' && formData.current !== null && formData.current !== undefined &&
            formData.target &&
            parseFloat(formData.current) >= parseFloat(formData.target)) {
            newErrors.target = 'L\'objectif doit être supérieur à la valeur actuelle';
        }

        if (!formData.valueUnit || (formData.valueUnit === 'custom' && !customUnit.trim())) {
            newErrors.valueUnit = 'Veuillez sélectionner ou saisir une unité';
        }

        if (!formData.timeline || formData.timeline <= 0) {
            newErrors.timeline = 'Veuillez saisir un délai valide';
        }

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const selectedCategoryData = categoryOptions.find(cat => cat.value === formData.category);
        const finalUnit = formData.valueUnit === 'custom' ? customUnit : formData.valueUnit;

        const objectiveData = {
            title: formData.title.trim(),
            category: formData.category === 'autres' ? formData.customCategory : selectedCategoryData?.label || formData.category,
            current: formData.valueType === 'text' ? formData.current : parseFloat(formData.current),
            target: formData.valueType === 'text' ? formData.target : parseFloat(formData.target),
            timeline: `${formData.timeline} ${formData.timelineUnit}`,
            confidence: formData.confidence,
            description: formData.description,
            valueUnit: finalUnit,
            valueType: formData.valueType,
            priority: formData.priority
        };

        await onSubmit(objectiveData);
    };

    const currentErrors = { ...errors, ...localErrors };

    return (
        <div>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* CHAMP TITRE */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Titre de l'objectif <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => onInputChange('title', e.target.value)}
                        className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.title ? 'border-red-300' : 'border-gray-300'
                            }`}
                        placeholder="Ex: Acquisition duplex Lyon, Formation négociation..."
                    />
                    {currentErrors.title && (
                        <p className="mt-1 text-xs text-red-600 flex items-center">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {currentErrors.title}
                        </p>
                    )}
                </div>

                {/* Sélection de catégorie - MASQUÉE si hideCategorySelector est true */}
                {!hideCategorySelector && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Catégorie d'objectif <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                            {categoryOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={() => onInputChange('category', option.value)}
                                    className={`p-3 bg-white border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm hover:bg-green-50 ${formData.category === option.value
                                        ? 'border-teal-500 bg-teal-50 shadow-sm'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0"></div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {option.label}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {currentErrors.category && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.category}
                            </p>
                        )}
                    </div>
                )}

                {/* Catégorie personnalisée */}
                {formData.category === 'autres' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom de la catégorie <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.customCategory}
                            onChange={(e) => onInputChange('customCategory', e.target.value)}
                            className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.customCategory ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="Ex: Épargne retraite, Projet personnel..."
                        />
                        {currentErrors.customCategory && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.customCategory}
                            </p>
                        )}
                    </div>
                )}

                {/* Sélection de priorité */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priorité <span className="text-gray-500">*</span>
                    </label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => onInputChange('priority', 'low')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${formData.priority === 'low'
                                    ? 'bg-gray-300 text-gray-800'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            Basse
                        </button>
                        <button
                            type="button"
                            onClick={() => onInputChange('priority', 'medium')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${formData.priority === 'medium'
                                    ? 'bg-gray-300 text-gray-800'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            Moyenne
                        </button>
                        <button
                            type="button"
                            onClick={() => onInputChange('priority', 'high')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${formData.priority === 'high'
                                    ? 'bg-gray-300 text-gray-800'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            Haute
                        </button>
                    </div>
                    {currentErrors.priority && (
                        <p className="mt-1 text-xs text-red-600 flex items-center">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {currentErrors.priority}
                        </p>
                    )}
                </div>

                {/* Sélection d'unité */}
                {selectedCategory && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unité de mesure <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowUnitDropdown(!showUnitDropdown)}
                                className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:bg-green-50 text-left flex items-center justify-between text-sm ${currentErrors.valueUnit ? 'border-red-300' : 'border-gray-300'
                                    }`}
                            >
                                <span className={formData.valueUnit ? 'text-gray-900' : 'text-gray-500'}>
                                    {formData.valueUnit ? selectedCategory.suggestedUnits.find(unit => unit.value === formData.valueUnit)?.label : 'Sélectionnez une unité'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showUnitDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown des unités */}
                            {showUnitDropdown && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                                    {/* Liste des unités */}
                                    <div className="max-h-48 overflow-y-auto">
                                        <button
                                            onClick={() => {
                                                onInputChange('valueUnit', '');
                                                setShowUnitDropdown(false);
                                            }}
                                            className={`w-full px-3 py-2 text-left text-xs hover:bg-green-50 transition-colors duration-150 ${!formData.valueUnit
                                                ? 'bg-green-100 text-green-800 font-medium'
                                                : 'text-gray-700 hover:text-green-700'
                                                }`}
                                        >
                                            Sélectionnez une unité
                                        </button>
                                        {selectedCategory.suggestedUnits.map((unit, index) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    onInputChange('valueUnit', unit.value);
                                                    onInputChange('valueType', unit.type || 'number');
                                                    setShowUnitDropdown(false);
                                                }}
                                                className={`w-full px-3 py-2 text-left text-xs hover:bg-green-50 transition-colors duration-150 ${formData.valueUnit === unit.value
                                                    ? 'bg-green-100 text-green-800 font-medium'
                                                    : 'text-gray-700 hover:text-green-700'
                                                    }`}
                                            >
                                                {unit.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Champ personnalisé pour unité custom */}
                        {formData.valueUnit === 'custom' && (
                            <input
                                type="text"
                                value={customUnit}
                                onChange={(e) => setCustomUnit(e.target.value)}
                                className="w-full px-3 py-2 h-9 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 mt-2 text-sm"
                                placeholder="Ex: points, niveaux, certifications..."
                            />
                        )}

                        {currentErrors.valueUnit && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.valueUnit}
                            </p>
                        )}

                    </div>
                )}

                {/* Valeurs actuelles et objectifs */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valeur actuelle
                        </label>
                        {formData.valueType === 'text' ? (
                            <input
                                type="text"
                                value={formData.current}
                                onChange={(e) => onInputChange('current', e.target.value)}
                                className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.current ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder={getPlaceholderWithUnit("Ex: Débutant, Intermédiaire")}
                            />
                        ) : (
                            <input
                                type="number"
                                value={formData.current}
                                onChange={(e) => onInputChange('current', e.target.value)}
                                className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.current ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder={getPlaceholderWithUnit("0")}
                                min={formData.valueType === 'percentage' ? "0" : "0"}
                                max={formData.valueType === 'percentage' ? "100" : undefined}
                                step={formData.valueType === 'percentage' ? "0.1" : "0.01"}
                            />
                        )}
                        {currentErrors.current && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.current}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Objectif <span className="text-red-500">*</span>
                        </label>
                        {formData.valueType === 'text' ? (
                            <input
                                type="text"
                                value={formData.target}
                                onChange={(e) => onInputChange('target', e.target.value)}
                                className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.target ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder={getPlaceholderWithUnit("Ex: Expert, Maîtrise avancée")}
                            />
                        ) : (
                            <input
                                type="number"
                                value={formData.target}
                                onChange={(e) => onInputChange('target', e.target.value)}
                                className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.target ? 'border-red-300' : 'border-gray-300'
                                    }`}
                                placeholder={getPlaceholderWithUnit("0")}
                                min={formData.valueType === 'percentage' ? "0" : "0"}
                                max={formData.valueType === 'percentage' ? "100" : undefined}
                                step={formData.valueType === 'percentage' ? "0.1" : "0.01"}
                            />
                        )}
                        {currentErrors.target && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.target}
                            </p>
                        )}
                    </div>
                </div>

                {/* Délai */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Délai <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            value={formData.timeline}
                            onChange={(e) => onInputChange('timeline', e.target.value)}
                            className={`w-full px-3 py-2 h-9 bg-white border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm ${currentErrors.timeline ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="12"
                            min="1"
                        />
                        {currentErrors.timeline && (
                            <p className="mt-1 text-xs text-red-600 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {currentErrors.timeline}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unité
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowTimelineDropdown(!showTimelineDropdown)}
                                className="w-full px-3 py-2 h-9 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:bg-green-50 text-left flex items-center justify-between text-sm"
                            >
                                <span className="text-gray-900">
                                    {timelineOptions.find(option => option.value === formData.timelineUnit)?.label || 'Sélectionnez une unité'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTimelineDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown des unités de temps */}
                            {showTimelineDropdown && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                                    {/* Liste des unités de temps */}
                                    <div className="max-h-48 overflow-y-auto">
                                        {timelineOptions.map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    onInputChange('timelineUnit', option.value);
                                                    setShowTimelineDropdown(false);
                                                }}
                                                className={`w-full px-3 py-2 text-left text-xs hover:bg-green-50 transition-colors duration-150 ${formData.timelineUnit === option.value
                                                    ? 'bg-green-100 text-green-800 font-medium'
                                                    : 'text-gray-700 hover:text-green-700'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Description (optionnelle) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description / Notes (optionnel)
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => onInputChange('description', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                        rows="2"
                        placeholder="Décrivez votre objectif, les étapes clés, ou toute information utile..."
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium"

                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Enregistrement...
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4 mr-2" />
                                {editMode ? 'Modifier l\'objectif' : 'Créer l\'objectif'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ObjectiveForm;